import { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Modal, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { LogoutOutlined, ReloadOutlined } from '@ant-design/icons';
import { listUserSessions, revokeUserSessions } from '@/api/uam';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { UamSessionView, UamUserView } from '@/api/types';
import { formatAbsolute } from '@/lib/time';

const { Paragraph, Text } = Typography;

/**
 * Live SSO sessions of one directory user (OPSFL-THIAM-1) — a Keycloak read-through over
 * GET/DELETE /v1/uam/users/{id}/sessions. Honest states throughout: an empty list means "not
 * signed in anywhere right now", a 403 means the caller's role holds no sessions key, and the
 * revoke explains its exact blast radius BEFORE the click — no new tokens from the killed
 * sessions, issued bearers age out at their short lifespan, and revoking your OWN sessions signs
 * you out everywhere (allowed by design — it is a logout, not an escalation).
 */
export function UserSessionsModal({
  target,
  selfUserId,
  onClose,
}: {
  target: UamUserView;
  selfUserId?: string;
  onClose: () => void;
}) {
  const { message, modal } = App.useApp();
  const isSelf = !!selfUserId && target.userId === selfUserId;

  const [sessions, setSessions] = useState<UamSessionView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const view = await listUserSessions(target.userId);
      setSessions(view.sessions);
    } catch (e) {
      const n = normalizeApiError(e);
      if (n.httpStatus === 403) {
        setError('Your role does not hold the session-read key (uam.sessions.read: hub admin, auditor).');
      } else if (n.code === 'UAM_USER_NOT_FOUND') {
        setError('That user no longer exists in this hub. Refresh the user list.');
      } else if (isNotAvailable(n)) {
        setError('This server does not serve the sessions endpoint yet (older build).');
      } else {
        setError(n.message);
      }
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [target.userId]);

  useEffect(() => {
    void load();
  }, [load]);

  const revokeAll = useCallback(() => {
    modal.confirm({
      title: `Sign ${target.username} out everywhere?`,
      icon: null,
      okText: 'Revoke all sessions',
      okButtonProps: { danger: true },
      content: (
        <div style={{ fontSize: 13 }}>
          <p style={{ margin: '4px 0' }}>
            Every live session is revoked: no new tokens can be minted from them. Access tokens
            already issued keep working for their short remaining lifespan — this is how OpenID
            Connect works, so treat revocation as &ldquo;stops within minutes&rdquo;, not instant.
            To block the account entirely, disable it as well.
          </p>
          {isSelf && (
            <p style={{ margin: '4px 0', fontWeight: 600 }}>
              This is your own account — you are signing yourself out everywhere, including this
              console. You will need to sign in again.
            </p>
          )}
        </div>
      ),
      onOk: async () => {
        setRevoking(true);
        try {
          const result = await revokeUserSessions(target.userId);
          message.success(
            result.revokedCount === 0
              ? 'No live sessions to revoke.'
              : `${result.revokedCount} session${result.revokedCount === 1 ? '' : 's'} revoked.`,
          );
          await load();
        } catch (e) {
          const n = normalizeApiError(e);
          if (n.httpStatus === 403) {
            message.error(
              'Your role does not hold the revoke key (uam.sessions.revoke: hub admin, a step-up lane).',
            );
          } else if (isNotAvailable(n)) {
            message.info('This server does not serve the sessions endpoint yet (older build).');
          } else {
            message.error(n.message);
          }
          throw e; // keep the confirm open on failure
        } finally {
          setRevoking(false);
        }
      },
    });
  }, [isSelf, load, message, modal, target.userId, target.username]);

  const columns: ColumnsType<UamSessionView> = [
    {
      title: 'Session',
      dataIndex: 'sessionId',
      key: 'sessionId',
      render: (v: string) => (
        <span className="ol-mono" style={{ fontSize: 11 }}>
          {v.length > 14 ? `${v.slice(0, 14)}…` : v}
        </span>
      ),
    },
    {
      title: 'IP address',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      render: (v?: string | null) =>
        v ? <span className="ol-mono" style={{ fontSize: 11.5 }}>{v}</span> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Started',
      dataIndex: 'started',
      key: 'started',
      render: (v?: string | null) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{formatAbsolute(v)}</Text>
      ),
    },
    {
      title: 'Last activity',
      dataIndex: 'lastAccess',
      key: 'lastAccess',
      render: (v?: string | null) => (
        <Text type="secondary" style={{ fontSize: 12 }}>{formatAbsolute(v)}</Text>
      ),
    },
    {
      title: 'Clients',
      dataIndex: 'clients',
      key: 'clients',
      render: (clients: string[]) =>
        clients.length ? (
          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
            {clients.map((c) => (
              <Tag key={c} bordered={false} style={{ margin: 0, fontSize: 10.5 }}>
                {c}
              </Tag>
            ))}
          </span>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
  ];

  return (
    <Modal
      title={`Sessions — ${target.username}`}
      open
      onCancel={onClose}
      width={680}
      footer={[
        <Button key="refresh" icon={<ReloadOutlined />} onClick={() => void load()} disabled={loading}>
          Refresh
        </Button>,
        <Button
          key="revoke"
          danger
          icon={<LogoutOutlined />}
          onClick={revokeAll}
          loading={revoking}
          disabled={loading || !!error || sessions.length === 0}
        >
          Sign out everywhere
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: 4 }}>
        Live sign-in sessions in this hub&rsquo;s identity realm, read directly from the directory
        (nothing is stored console-side). Revoking is per-user and total — there is no per-session
        revoke on this surface.
      </Paragraph>
      {error ? (
        <Alert type="warning" showIcon message="Sessions unavailable" description={error} />
      ) : (
        <Table<UamSessionView>
          rowKey="sessionId"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={sessions}
          pagination={false}
          locale={{
            emptyText: 'No live sessions — this user is not signed in anywhere right now.',
          }}
        />
      )}
    </Modal>
  );
}
